import { useEffect, useState } from "react"

import { Interaction, InteractionRef, InteractionStatus } from "@vertesia/common"
import { JSONSchema4Object } from "json-schema"
import { ArrowUp, ArrowLeft } from "lucide-react"

import { AgentAccessModeBadge } from "@components/badges/AgentAccessModeBadge"
import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { AgentRunnerPayloadPanel } from "@components/panels/agent-runner/AgentRunnerPayloadPanel"
import { AgentModelSelection } from "@components/selections/AgentModelSelection"

import FormState from "@plugins/form/FormState"
import { ManagedObject } from "@plugins/form/ManagedObject"

import { useUserSession } from "@session/UserSession"

import { Badge } from "@ui/badge"
import { Button } from "@ui/button"

interface AgentPanelProps {
    interactionRef: InteractionRef | undefined
    onBackToSearch: () => void
    onPayloadReady: (interaction: Interaction, accessType: "private" | "shared", data: JSONSchema4Object | undefined) => void
}
export function AgentPanel({ interactionRef, onBackToSearch, onPayloadReady }: Readonly<AgentPanelProps>) {
    const { client } = useUserSession()

    const [formState, setFormState] = useState<FormState>()
    const [interaction, setInteraction] = useState<Interaction | undefined>()
    const [payload, setPayload] = useState<ManagedObject | undefined>(undefined)

    const [accessType, setAccessType] = useState<"private" | "shared">("private")

    const [model, setModel] = useState<string | undefined>(undefined)

    const onAccessTypeChange = (value: boolean) => {
        setAccessType(value ? "shared" : "private")
    }

    const handlePayloadReady = () => {
        if (!interaction) {
            return
        }

        onPayloadReady(interaction, accessType, payload?.value ?? (interaction.test_data as JSONSchema4Object))
    }

    const handlePayloadUpdate = (payload: ManagedObject) => {
        setPayload(payload)
    }

    useEffect(() => {
        if (!client) {
            return
        }

        if (!interactionRef) {
            setFormState(undefined)
            return
        }

        client.interactions.retrieve(interactionRef.id).then((interaction) => {
            setInteraction(interaction as Interaction)
            setModel(interaction.model)
            setFormState(new FormState(client, interaction as Interaction))
        })
    }, [interactionRef])

    return (
        <div className="w-full h-full overflow-hidden flex flex-col">
            <div className="w-full flex flex-row gap-2 items-center justify-between p-3 border-b">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onBackToSearch} className="hover:bg-gray-100 transition-colors duration-200">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <div className="text-2xl font-semibold truncate">{interactionRef?.name || "Browse Services"}</div>
                </div>
                {interactionRef && (
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                            v{interactionRef.version}
                        </Badge>
                        <Badge variant={interactionRef.status === InteractionStatus.published ? "default" : "secondary"} className="text-xs">
                            {interactionRef.status}
                        </Badge>
                    </div>
                )}
            </div>
            <div className="w-full h-full flex flex-col max-w-4xl mx-auto items-center justify-center px-4 pt-8 pb-4 gap-4">
                <div className="flex flex-col items-center w-full bg-white border border-gray-300 rounded-3xl p-4 gap-2">
                    {formState && <FormState.Provider value={formState}>{interaction && <AgentRunnerPayloadPanel onUpdate={handlePayloadUpdate} />}</FormState.Provider>}
                    <div className="flex items-center justify-end w-full mt-4 px-2 pb-2">
                        <div className="flex items-center justify-between w-full gap-2">
                            <div className="flex gap-2">
                                <AgentModelSelection disabled model={model} />
                                <AgentAccessModeBadge value={accessType} onChange={onAccessTypeChange} />
                            </div>
                            <ActionTooltipButton disabled={!interaction} icon={<ArrowUp />} variant="icon" size="icon" tooltip="Send message" onClick={handlePayloadReady} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
